import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import axios from "axios";
import * as Sentry from "@sentry/nextjs";
import { Row, Col } from "react-bootstrap";

import { PROCESSING_CODES } from "../../../../../constants/payment";
import { FORM_FIELD_KEYS, PROCESSING_OPTIONS_MAP } from "../../../../../constants/forms";
import { getFieldProps } from "../../../../../utils/form-fields";
import { hexToRGBA } from "../../../../../utils/theme";
import { usePrevious } from "../../../../../hooks";

import ProcessingRadioGroup from "./ProcessingRadioGroup";

const StyledContainer = styled.div`
  margin-bottom: 24px;
`;

const StyledRow = styled(Row)`
  margin-top: 1rem;
  margin-bottom: 2rem;
  margin-right: 0;
  margin-left: 0;
  padding: 16px;
  border: 2px solid #797c7f;
`;

const LEAD_DISCOUNT_CODES = [
  PROCESSING_CODES.leadDiscount,
  PROCESSING_CODES.leadDiscount20,
  PROCESSING_CODES.leadDiscount50,
];

function ProcessingBlock({ values = {}, onChange }) {
  const { query, isReady } = useRouter();
  const [rawOptions, setRawOptions] = useState([]);
  const [pricesMap, setPricesMap] = useState(new Map());
  const [discountProcessingCode, setDiscountProcessingCode] = useState(null);
  const [discountCouponType, setDiscountCouponType] = useState(null);

  const prevValues = usePrevious(values);
  const prevPriceMapSize = usePrevious(pricesMap.size);

  const processingOptions = useMemo(() => {
    return rawOptions
      .filter(({ code }) => discountProcessingCode ? code === discountProcessingCode : !LEAD_DISCOUNT_CODES.includes(code))
      .map(({ code, value }) => {
        if (PROCESSING_OPTIONS_MAP.has(code)) {
          return {
            ...PROCESSING_OPTIONS_MAP.get(code),
            name: discountCouponType ? `EIN Application - ${discountCouponType.split('_')[1] || discountCouponType} % Limited Time Discount` : PROCESSING_OPTIONS_MAP.get(code).name,
            price: pricesMap.get(code),
          };
        }

        return {
          name: value,
          value: code,
          price: pricesMap.get(code),
        };
      });
  }, [rawOptions, pricesMap, discountProcessingCode, discountCouponType]);

  useEffect(() => {
    if (prevValues?.processingOption !== values.processingOption) {
      onChange && onChange({
        target: {
          name: "currentPrice",
          value: pricesMap.get(values.processingOption) || 0,
        },
      });
    }
  }, [onChange, values, prevValues, pricesMap]);

  useEffect(() => {
    if (prevPriceMapSize !== pricesMap.size) {
      onChange && onChange({
        target: {
          name: "currentPrice",
          value: pricesMap.get(values.processingOption) || 0,
        },
      });
    }
  }, [onChange, values, prevPriceMapSize, pricesMap]);

  useEffect(() => {
    axios.get(process.env.NEXT_PUBLIC_REACT_APP_API + "/category/EIN/processing-options?onlyActive=true")
      .then((response) => {
        const optionTempSet = new Set();

        response.data.values.forEach((option) => {
            optionTempSet.add(option.code);
        });

        setRawOptions([...(response.data.values || [])]);

        if (!optionTempSet.has(PROCESSING_CODES.rush) && optionTempSet.has(PROCESSING_CODES.standard)) {
          onChange && onChange({ target: { name: FORM_FIELD_KEYS.processingOption, value: PROCESSING_CODES.standard } });
        }
      })
      .catch((error) => {
        let salePage = window.location.href;
        let salePageURLArr = salePage.split("/");
        let salePageURL = (salePageURLArr[0] + "//" + salePageURLArr[2]).replace(/\/+$/, "");
        axios.get(salePageURL + "/crm/category/EIN/processing-options?onlyActive=true")
          .then((response) => {
            const optionTempSet = new Set();

            response.data.values.forEach((option) => {
                optionTempSet.add(option.code);
            });
    
            setRawOptions([...(response.data.values || [])]);
    
            if (!optionTempSet.has(PROCESSING_CODES.rush) && optionTempSet.has(PROCESSING_CODES.standard)) {
              onChange && onChange({ target: { name: FORM_FIELD_KEYS.processingOption, value: PROCESSING_CODES.standard } });
            }
          })
          .catch((error) => {
            console.log(error.response);
          });
    });
  }, [onChange]);

  useEffect(() => {
    if (query.order && query.token) {
      axios.get(process.env.NEXT_PUBLIC_REACT_APP_API + "/orders/" + query.order, { headers: { Authorization: "Bearer " + query.token } })
        .then((response) => {
          onChange && onChange({ target: { name: FORM_FIELD_KEYS.processingOption, value: PROCESSING_CODES.standard } }, {
            currentOrder: response.data.order,
            orderId: response.data.order.id,
            token: query.token,
            campaignId: query.campaignId || null,
            sourceValue1: query.sourceValue1 || null,
            sourceValue2: query.sourceValue2 || null,
            sourceValue3: query.sourceValue3 || null,
            sourceValue4: query.sourceValue4 || null,
            sourceValue5: query.sourceValue5 || null,
          });

          setDiscountProcessingCode(PROCESSING_CODES.standard);

          axios.get(process.env.NEXT_PUBLIC_REACT_APP_API + "/tokens", { headers: { Authorization: "Bearer " + query.token } })
            .then(tokenDiscountResponse => {
              setDiscountCouponType(tokenDiscountResponse.data.discountCouponType);
              onChange && onChange({ target: { name: "discountCouponType", value: tokenDiscountResponse.data.discountCouponType } });
            })
            .catch((error) => {
              console.log(error);
            });
        })
        .catch((error) => {
          if (error.response) {
            onChange && onChange({ target: { name: "discountError", value: error } });

            if (error.response.status !== 403) {
              Sentry.captureException(new Error("Token error"), {
                tags: {
                  section: "products",
                },
              });
            }
          } else if (error.request) {
            let salePage = window.location.href;
            let salePageURLArr = salePage.split("/");
            let salePageURL = (salePageURLArr[0] + "//" + salePageURLArr[2]).replace(/\/+$/, "");

            axios.get(salePageURL + "/crm/orders/" + query.order, { headers: { Authorization: "Bearer " + query.token } })
              .then((response2) => {
                onChange && onChange({ target: { name: FORM_FIELD_KEYS.processingOption, value: PROCESSING_CODES.standard } }, {
                  currentOrder: response2.data.order,
                  orderId: response2.data.order.id,
                  token: query.token,
                  campaignId: query.campaignId || null,
                  sourceValue1: query.sourceValue1 || null,
                  sourceValue2: query.sourceValue2 || null,
                  sourceValue3: query.sourceValue3 || null,
                  sourceValue4: query.sourceValue4 || null,
                  sourceValue5: query.sourceValue5 || null,
                });

                setDiscountProcessingCode(PROCESSING_CODES.standard);

                axios.get(salePageURL + "/tokens", { headers: { Authorization: "Bearer " + query.token } })
                  .then(tokenDiscountResponse => {
                    setDiscountCouponType(tokenDiscountResponse.data.discountCouponType);
                    onChange && onChange({ target: { name: "discountCouponType", value: tokenDiscountResponse.data.discountCouponType } });
                  })
                  .catch((error) => {
                    console.log(error);
                  });
              })
              .catch((error) => {
                onChange && onChange({ target: { name: "discountError", value: error } });
        
                if (error.response.status !== 403) {
                  Sentry.captureException(new Error("Token error"), {
                    tags: {
                      section: "products",
                    },
                  });
                }
                console.log(error);
              });
          } else {
            onChange && onChange({ target: { name: "discountError", value: error } });
          
            Sentry.captureException(new Error("Token error"), {
              tags: {
                section: "products",
              },
            });
          }
          console.log(error);
        });
    }
  }, [query, onChange]);

  useEffect(() => {
    if (isReady && query.token && discountCouponType || isReady && !query.token) {
      const params = { productCodes: "EIN_SoleProp" };

      if (discountCouponType) {
        params.discountCouponType = discountCouponType;
      }
  
      axios.get(process.env.NEXT_PUBLIC_REACT_APP_API + "/category/EIN/products/prices", {
        params,
        headers: { "Content-Type": "application/json" }
      })
        .then((response) => {
          const pricesTempMap = new Map();
  
          response.data.forEach((product) => {
            pricesTempMap.set(product.option, product.price);
          });
  
          setPricesMap(pricesTempMap);
        })
        .catch((error) => {
          let salePage = window.location.href;
          let salePageURLArr = salePage.split("/");
          let salePageURL = (salePageURLArr[0] + "//" + salePageURLArr[2]).replace(/\/+$/, "");
  
          axios.get(salePageURL + "/category/EIN/products/prices", {
            params,
            headers: { "Content-Type": "application/json" }
          })
            .then((response) => {
              const pricesTempMap = new Map();
      
              response.data.forEach((product) => {
                pricesTempMap.set(product.option, product.price);
              });
      
              setPricesMap(pricesTempMap);
            })
            .catch((error) => {
              console.log(error);
            });
        });
    }
  }, [discountCouponType, isReady, query]);

  return (
    <Row>
      <Col xs={12}>
        <div className="page-title">
          <span>Submit Your EIN Application</span>
        </div>
      </Col>
      <Col xs={12}>
        <StyledContainer>
          <div className="inputs-block-title">
            <span>Processing Option</span>
          </div>
          <StyledRow>
          <Col xs={12}>
            <ProcessingRadioGroup
              {...getFieldProps(FORM_FIELD_KEYS.processingOption)}
              value={values[FORM_FIELD_KEYS.processingOption]}
              options={processingOptions}
              onChange={onChange}
            />
          </Col>
          </StyledRow>
        </StyledContainer>
      </Col>
    </Row>
  );
}

export default ProcessingBlock;
